import type { BlogPost, BlogPostSummary } from '@wanderlust/shared';
import { api } from './api';

/**
 * Posts already fetched this session, so a reader who follows a link from the
 * blog index lands on the article instead of a loading state. Cards warm this
 * on hover, and the article page fills it in on the way past.
 *
 * Bounded because a long browse would otherwise hold every post body it ever
 * touched; oldest-first eviction is enough for a list this small.
 */
const MAX_ENTRIES = 20;

const posts = new Map<string, BlogPost>();

/**
 * The request for each post still on the wire, so everyone who wants a post
 * that's already coming waits on the one request instead of starting another.
 * A card's hover prefetch and the click that lands on the article a moment
 * later are the common case, and the slower the response the wider that window.
 */
const inFlight = new Map<string, Promise<BlogPost>>();

export function getCachedBlogPost(slug: string | undefined): BlogPost | null {
  if (!slug) return null;
  return posts.get(slug) ?? null;
}

export function cacheBlogPost(post: BlogPost): void {
  if (posts.size >= MAX_ENTRIES) {
    const oldest = posts.keys().next().value;
    if (oldest !== undefined) posts.delete(oldest);
  }
  posts.set(post.slug, post);
}

/** Exposed for tests, which share a module registry across cases. */
export function clearBlogPostCache(): void {
  posts.clear();
  inFlight.clear();
}

/**
 * The post: from this session's cache when we already have it, from the request
 * already in flight when one is, and over the network otherwise.
 */
export function loadBlogPost(slug: string): Promise<BlogPost> {
  const cached = posts.get(slug);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(slug);
  if (pending) return pending;

  const request = api
    .getBlogPost(slug)
    .then((post) => {
      cacheBlogPost(post);
      return post;
    })
    .finally(() => {
      // Cleared either way: a post that failed once should be retried on the
      // next ask rather than handing back the same rejection forever.
      inFlight.delete(slug);
    });

  // A prefetch may be the only caller, and it doesn't wait on the result, so
  // claim the rejection here rather than let it surface as an unhandled one.
  void request.catch(() => {});

  inFlight.set(slug, request);
  return request;
}

/**
 * Readers on a metered connection asked not to be spent on things they haven't
 * chosen yet, and a post body plus its route chunk is a real download.
 */
function prefetchingIsWelcome(): boolean {
  if (typeof navigator === 'undefined') return false;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return connection?.saveData !== true;
}

/**
 * Start loading a post before it's asked for. Covers both halves of the wait a
 * click would otherwise show: the lazy route chunk and the post itself.
 *
 * Failures are swallowed on purpose — a prefetch that doesn't land just leaves
 * the page to fetch normally, and surfacing the error would report a request
 * the reader never made.
 */
export function prefetchBlogPost(slug: string): void {
  if (!slug || posts.has(slug) || inFlight.has(slug) || !prefetchingIsWelcome()) return;

  // Resolves to the same module main.tsx lazy-loads, so this warms that chunk.
  void import('../pages/BlogPost.tsx').catch(() => {});

  void loadBlogPost(slug);
}

/**
 * What a card hands the article page through the link it was clicked on. The
 * summary is everything but the body, which is enough to paint the header,
 * hero, and byline immediately — so a cold click (no hover, so no prefetch,
 * which is every touch device) still opens onto the article rather than a
 * placeholder page.
 */
export interface BlogPostLinkState {
  blogPostSummary: BlogPostSummary;
}

export function blogPostLinkState(post: BlogPostSummary): BlogPostLinkState {
  return { blogPostSummary: post };
}

/**
 * The summary carried by the link that got us here, if it's for the post being
 * viewed. Guarded on the slug because history entries outlive the navigation
 * that set them: a back button can restore state from a different post.
 */
export function readBlogPostLinkState(
  state: unknown,
  slug: string | undefined,
): BlogPostSummary | null {
  if (!slug || !state || typeof state !== 'object') return null;

  const summary = (state as Partial<BlogPostLinkState>).blogPostSummary;
  return summary?.slug === slug ? summary : null;
}
